import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const HowWeHelpSection = ({ headerData, serviceCards, statsDataHeading, statsData, worldMapImg }) => {
  const sectionRef = useRef(null);
  const headerTitleRef = useRef(null);
  const headerHighlightedTextRef = useRef(null);
  const headerNormalTextRef = useRef(null);
  const headerDescriptionRef = useRef(null);
  const headerImgRef = useRef(null);
  const serviceCardRefs = useRef([]);
  const statsHeadingRef = useRef(null);
  const statBlockRefs = useRef([]);
  const worldMapImgRef = useRef(null);
  const animationTriggered = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !animationTriggered.current) {
            animationTriggered.current = true;
            animateSection();
            if (sectionRef.current) {
              observer.unobserve(sectionRef.current);
            }
          }
        });
      },
      {
        threshold: 0,
      }
    );

    const currentRef = sectionRef.current;

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  const animateSection = () => {
    const tl = gsap.timeline({ defaults: { duration: 0.5, ease: "power2.out" } });

    // Header Section Animation
    tl.fromTo(headerTitleRef.current, { yPercent: -20, opacity: 0 }, { yPercent: 0, opacity: 1, duration: 0.4 }, "+=0.1");
    tl.fromTo(headerHighlightedTextRef.current, { yPercent: -20, opacity: 0 }, { yPercent: 0, opacity: 1, duration: 0.4 }, "-=0.4");
    tl.fromTo(headerNormalTextRef.current, { yPercent: -20, opacity: 0 }, { yPercent: 0, opacity: 1, duration: 0.4 }, "-=0.4");
    tl.fromTo(headerDescriptionRef.current, { yPercent: 20, opacity: 0 }, { yPercent: 0, opacity: 1, duration: 0.5 }, "-=0.4");
    tl.fromTo(headerImgRef.current, { xPercent: 20, opacity: 0 }, { xPercent: 0, opacity: 1, duration: 0.5 }, "-=0.6");

    // Service Cards Grid Animation
    tl.fromTo(
      serviceCardRefs.current,
      { yPercent: 20, opacity: 0 },
      { yPercent: 0, opacity: 1, stagger: 0.08, duration: 0.4 },
      "+=0.2"
    );

    // Why Work With Us Section Animation
    tl.fromTo(statsHeadingRef.current, { yPercent: -20, opacity: 0 }, { yPercent: 0, opacity: 1, duration: 0.4 }, "+=0.2");
    tl.fromTo(
      statBlockRefs.current,
      { yPercent: 20, opacity: 0 },
      { yPercent: 0, opacity: 1, stagger: 0.1, duration: 0.4 },
      "+=0.1"
    );
    tl.fromTo(worldMapImgRef.current, { opacity: 0 }, { opacity: 1, duration: 0.5 }, "+=0.1");
  };

  return (
    <div className="max-w-6xl mx-auto md:px-4 md:pt-20 pt-8" ref={sectionRef}>
      {/* Header Section */}
      <div className="flex flex-col md:flex-row items-center text-left justify-between md:mb-20 mb-12">
        <div className="md:w-1/2 mb-8 md:mb-0">
          <h2 className="text-4xl md:text-5xl leading-tight text-black mb-2" ref={headerTitleRef}>{headerData.title}</h2>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="text-gray-900" ref={headerHighlightedTextRef}>{headerData.highlightedText}</span>
            <span className="text-black font-medium" ref={headerNormalTextRef}> {headerData.normalText}</span>
          </h1>
          <p className="text-black text-md max-w-lg" ref={headerDescriptionRef}>
            {headerData.description}
          </p>
        </div>
        <div className="md:w-1/3" ref={headerImgRef}>
          <img src={headerData.imgLink} alt="Partnership" className="w-full" />
        </div>
      </div>

      {/* Service Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 md:gap-1 gap-4 md:mb-16 mb-12 max-w-5xl mx-auto">
        {serviceCards.map((card, index) => {
          const bgImage = index % 4 === 0 ? '/img/bgd1.png' :
            index % 4 === 1 ? '/img/bgd2.png' :
              index % 4 === 2 ? '/img/bgd2.png' : '/img/bgd1.png';

          return (
            <div
              key={index}
              ref={(el) => (serviceCardRefs.current[index] = el)}
              className="md:p-20 p-8 h-96 relative overflow-hidden text-white flex flex-col md:flex-row items-center justify-center"
              style={{
                backgroundImage: `url(${bgImage})`,
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center right'
              }}
            >
              <div className="flex w-full flex-col md:flex-row relative z-10 items-end md:items-start"> 
                <div className="text-left md:pr-4 mb-4 md:mb-0">
                  <h3 className="text-4xl font-medium mb-6">{card.title}</h3>
                  <p className="md:mb-6 text-md opacity-90">{card.text}</p>
                </div>
                <div className="text-right md:text-left flex items-center justify-end">
                  <img
                    src={card.iconLink}
                    alt={card.title}
                    className="block md:ml-0 md:w-32 md:h-auto"
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Why Work With Us Section */}
      <div className="my-0 text-center md:text-center">
        <h2 className="text-4xl md:text-5xl leading-tight mb-4 text-black" ref={statsHeadingRef}>
          {statsDataHeading.light} <span className="font-bold">{statsDataHeading.bold}</span>
        </h2>

        <div className="flex flex-col md:flex-row justify-between mb-8 items-center md:items-start">
          {statsData.map((stat, index) => (
            <div key={index} ref={(el) => (statBlockRefs.current[index] = el)} className={`md:my-8 my-4 md:mb-0 md:w-1/3 px-8 ${index !== statsData.length - 1 ? 'md:border-r border-gray-300 md:pr-8' : ''}`}>
              {stat.title ? <h3 className="text-3xl font-medium mb-1 text-center md:text-left">{stat.title}</h3> : <img src={stat.img} className='mb-4 mx-auto block' alt={stat.text} />}
              <p className="text-black text-md text-center md:text-left">{stat.text}</p>
            </div>
          ))}
        </div>

        <div className="w-full" ref={worldMapImgRef}>
          <img src={worldMapImg} alt="Global Growth" className="w-full mx-auto block" />
        </div>
      </div>
    </div>
  );
};

export default HowWeHelpSection;